import { z } from 'zod';
import { TreeDeviceSchema } from '../../devices/schemas/devices.schemas.ts';
import {
  createPositiveNumberStringSchema,
  hasMinLength,
  hasMaxLength,
  ValidationMessages,
  isValidLongitude,
  isValidLatitude,
  isValidPhoneNumber,
  isValidNumber,
  isPositiveNumber,
} from '@/utils/validation-utils.ts';

const FIELD_LIMITS = {
  PLANT_NAME: { min: 2, max: 30 },
  SHORT_DESCRIPTION: { max: 250 },
} as const;

export const SETUP_TYPES = {
  PRODUCTION_ONLY: 'production-only',
  PRODUCTION_STORAGE: 'production-storage',
  STORAGE_ONLY: 'storage-only',
} as const;

export const storageChemistryOptions = [
  { label: 'NMC (Nickel Manganese Cobalt)', value: 'NMC' },
  { label: 'LFP (Lithium Iron Phosphate)', value: 'LFP' },
  { label: 'LMX (Lithium Manganese Oxide)', value: 'LMX' },
] as const;

export const PLANT_STATUS = [
  'Operational',
  'Planning',
  'Under Construction',
  'Maintenance',
  'Archived',
] as const;

export const storageChemistryOptionsArray = storageChemistryOptions.map(
  (option) => option.value,
);

const basicDetailsFormSchema = z.object({
  name: z
    .string({ error: ValidationMessages.required('Plant name') })
    .refine(
      hasMinLength(FIELD_LIMITS.PLANT_NAME.min),
      ValidationMessages.minLength('Plant name', FIELD_LIMITS.PLANT_NAME.max),
    )
    .refine(
      hasMaxLength(FIELD_LIMITS.SHORT_DESCRIPTION.max),
      ValidationMessages.maxLength('Plant name', FIELD_LIMITS.SHORT_DESCRIPTION.max),
    ),
  type: z
    .string({ error: ValidationMessages.required('Plant type') })
    .min(1, { message: ValidationMessages.required('Plant type') }),
  status: z.enum(PLANT_STATUS),
  shortDescription: z
    .string({ error: ValidationMessages.required('Short description') })
    .refine(
      hasMaxLength(FIELD_LIMITS.SHORT_DESCRIPTION.max),
      ValidationMessages.maxLength('Short description', FIELD_LIMITS.SHORT_DESCRIPTION.max),
    )
    .optional(),
});

const siteInformationFormSchema = z.object({
  image: z.array(z.instanceof(File)).optional().or(z.string()),
  country: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('Country'),
    },
  ),
  state: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('State'),
    },
  ),
  city: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('City'),
    },
  ),
  siteAddress: z.string().optional(),
  coordinates: z.object({
    longitude: z
      .string({ error: ValidationMessages.required('Longitude') })
      .min(1, { message: ValidationMessages.required('Longitude') })
      .refine(isValidLongitude, ValidationMessages.invalidLongitude),
    latitude: z
      .string({ error: ValidationMessages.required('Latitude') })
      .min(1, { message: ValidationMessages.required('Latitude') })
      .refine(isValidLatitude, ValidationMessages.invalidLatitude),
  }),
  timezone: z.object(
    {
      value: z.string(),
      label: z.string(),
    },
    {
      error: ValidationMessages.required('Timezone'),
    },
  ),
  siteArea: createPositiveNumberStringSchema({
    fieldName: 'Site area',
    required: false,
  }),
  siteAccess: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional(),
});

const technicalSpecificationsFormSchema = z
  .object({
    setupType: z.enum([
      SETUP_TYPES.PRODUCTION_STORAGE,
      SETUP_TYPES.PRODUCTION_ONLY,
      SETUP_TYPES.STORAGE_ONLY,
    ]),
    pvCapacity: createPositiveNumberStringSchema({
      fieldName: 'PV Capacity',
      required: false,
    }),
    pvInvertingPower: createPositiveNumberStringSchema({
      fieldName: 'PV Inverting Power',
      required: false,
    }),
    storageCapacity: createPositiveNumberStringSchema({
      fieldName: 'Storage Capacity',
      required: false,
    }),
    storageChemistry: z
      .array(z.string(), { error: 'Storage Chemistry is required' })
      .optional(),
    storageInvertingPower: createPositiveNumberStringSchema({
      fieldName: 'Storage Inverting Power',
      required: false,
    }),

    // deprecated - renamed to pvCapacity
    capacity: createPositiveNumberStringSchema({
      fieldName: 'Capacity',
      required: false,
    }),
  })
  .refine(
    (data) => {
      if (data.setupType === SETUP_TYPES.STORAGE_ONLY) return true;

      return (
        data.pvCapacity && isValidNumber(data.pvCapacity) && isPositiveNumber(data.pvCapacity)
      );
    },
    {
      message: 'PV Capacity is required and must be a positive number',
      path: ['pvCapacity'],
    },
  )
  .refine(
    (data) => {
      if (data.setupType === SETUP_TYPES.PRODUCTION_ONLY) return true;

      return (
        data.storageCapacity &&
        isValidNumber(data.storageCapacity) &&
        isPositiveNumber(data.storageCapacity)
      );
    },
    {
      message: 'Storage Capacity is required and must be a positive number',
      path: ['storageCapacity'],
    },
  );

const financialInformationFormSchema = z.object({
  tariffRate: createPositiveNumberStringSchema({ fieldName: 'Tariff rate', required: false }),
  expectedROI: createPositiveNumberStringSchema({
    fieldName: 'Expected ROI',
    required: false,
  }),
  operationalCosts: createPositiveNumberStringSchema({
    fieldName: 'Operational costs',
    required: false,
  }),
});

const contactDetailsFormSchema = z.object({
  owner: z.object({
    firstName: z
      .string({ error: ValidationMessages.required('Owner first name') })
      .min(1, { message: ValidationMessages.required('Owner first name') }),
    lastName: z
      .string({ error: ValidationMessages.required('Owner last name') })
      .min(1, { message: ValidationMessages.required('Owner last name') }),
  }),
  ownerEmail: z
    .string({ error: ValidationMessages.required('Owner email') })
    .email(ValidationMessages.invalidEmail)
    .min(1, { message: ValidationMessages.required('Owner email') }),
  ownerPhoneNumber: z
    .string({ error: ValidationMessages.required('Owner phone number') })
    .min(1, { message: ValidationMessages.required('Owner phone number') })
    .refine(isValidPhoneNumber, ValidationMessages.invalidPhone),

  installer: z
    .string({ error: ValidationMessages.required('Installer name') })
    .min(1, { message: ValidationMessages.required('Installer name') }),
  installerEmail: z
    .string({ error: ValidationMessages.required('Installer email') })
    .email(ValidationMessages.invalidEmail)
    .min(1, { message: ValidationMessages.required('Installer email') }),
  installerPhoneNumber: z
    .string({ error: ValidationMessages.required('Installer phone number') })
    .min(1, { message: ValidationMessages.required('Installer phone number') })
    .refine(isValidPhoneNumber, ValidationMessages.invalidPhone),
});

export const PlantSchema = z.object({
  plantId: z.string(),
  companyId: z.string(),

  basicDetails: basicDetailsFormSchema,
  siteInformation: siteInformationFormSchema,
  technicalSpecifications: technicalSpecificationsFormSchema,
  equipmentAndConnectionDetails: z.object({
    devices: z.array(TreeDeviceSchema),
  }),
  financialInformation: financialInformationFormSchema,
  contactDetails: contactDetailsFormSchema,

  dataCollectionInterval: z.string(),

  createdAt: z.string(),
  updatedAt: z.string(),
});

export type TPlant = z.infer<typeof PlantSchema>;
